import { createTheme } from "@mui/material/styles"

// Single source of truth for the two theme palettes.
// applyCssPalette() writes these onto <html> as CSS variables at runtime;
// the :root / [data-theme="dark"] blocks in index.css are only a
// first-paint fallback and should mirror these values.
export const palettes = {
  light: {
    bg: "#e2ddd0",
    surface: "#f7f5f0",
    surface2: "#ffffff",
    border: "#d8d3c6",
    text: "#23262e",
    muted: "#6f6a60",
    accent: "#a07c2c",
    accentSoft: "rgba(160, 124, 44, 0.12)",
    accentGlow: "rgba(160, 124, 44, 0.55)",
    accentContrast: "#ffffff",
    danger: "#b3261e",
    success: "#2e7d32",
    successBright: "#7bd88a",
    pillBg: "rgba(20, 23, 30, 0.9)",
    pillText: "#ffffff",
    pillMuted: "#c9ced6",
    pillRing: "rgba(255, 255, 255, 0.3)",
    pillLink: "#eebd63",
    shadow1: "0 1px 3px rgba(20, 18, 12, 0.12)",
    shadow2: "0 4px 16px rgba(20, 18, 12, 0.18)",
    cardShadow: "0 1px 6px rgba(20, 18, 12, 0.28)",
    // recessed lineup wells (champion picker playmat zones)
    wellBg: "rgba(20, 18, 12, 0.05)",
    wellInset: "inset 0 1px 5px rgba(20, 18, 12, 0.1)",
  },
  dark: {
    bg: "#0c0e13",
    surface: "#1a1e26",
    surface2: "#232833",
    border: "#303645",
    text: "#e9e7e2",
    muted: "#949daa",
    accent: "#d9a84e",
    accentSoft: "rgba(217, 168, 78, 0.14)",
    accentGlow: "rgba(217, 168, 78, 0.6)",
    accentContrast: "#221803",
    danger: "#e5736c",
    success: "#2e7d32",
    successBright: "#7bd88a",
    pillBg: "rgba(10, 12, 16, 0.92)",
    pillText: "#ffffff",
    pillMuted: "#c9ced6",
    pillRing: "rgba(255, 255, 255, 0.32)",
    pillLink: "#eebd63",
    shadow1: "0 1px 3px rgba(0, 0, 0, 0.4)",
    shadow2: "0 4px 16px rgba(0, 0, 0, 0.5)",
    // "bright shadow": faint light ring + glow so black card
    // borders don't melt into the dark background
    cardShadow:
      "0 0 0 1px rgba(255, 255, 255, 0.14), 0 0 14px rgba(255, 255, 255, 0.07)",
    // lighter fill in dark mode — the cards' black borders would
    // melt into a darker-than-surface well
    wellBg: "rgba(255, 255, 255, 0.22)",
    wellInset: "inset 0 1px 6px rgba(0, 0, 0, 0.5)",
  },
}

const cssVarNames = {
  bg: "--bg",
  surface: "--surface",
  surface2: "--surface-2",
  border: "--border",
  text: "--text",
  muted: "--muted",
  accent: "--accent",
  accentSoft: "--accent-soft",
  accentGlow: "--accent-glow",
  accentContrast: "--accent-contrast",
  danger: "--danger",
  success: "--success",
  successBright: "--success-bright",
  pillBg: "--pill-bg",
  pillText: "--pill-text",
  pillMuted: "--pill-muted",
  pillRing: "--pill-ring",
  pillLink: "--pill-link",
  shadow1: "--shadow-1",
  shadow2: "--shadow-2",
  cardShadow: "--card-shadow",
  wellBg: "--well-bg",
  wellInset: "--well-inset",
}

export const applyCssPalette = (mode) => {
  const p = palettes[mode] ?? palettes.light
  const root = document.documentElement
  root.dataset.theme = mode
  for (const [key, varName] of Object.entries(cssVarNames)) {
    root.style.setProperty(varName, p[key])
  }
}

export const getTheme = (mode) => {
  const p = palettes[mode] ?? palettes.light
  return createTheme({
    palette: {
      mode,
      primary: { main: p.accent, contrastText: p.accentContrast },
      background: { default: p.bg, paper: p.surface },
      text: { primary: p.text, secondary: p.muted },
      divider: p.border,
      error: { main: p.danger },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"IBM Plex Sans", system-ui, -apple-system, sans-serif',
      fontSize: 13,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: "none", fontWeight: 500 },
        },
      },
      MuiSwitch: {
        defaultProps: { size: "small" },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            fontSize: 12,
            lineHeight: 1.5,
            backgroundColor: mode === "dark" ? "#2c313c" : "#3a3f4a",
          },
          arrow: {
            color: mode === "dark" ? "#2c313c" : "#3a3f4a",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: p.surface2,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: p.surface2,
            border: `1px solid ${p.border}`,
          },
        },
      },
      MuiChip: {
        defaultProps: { size: "small" },
      },
    },
  })
}
